// UrbanVista Web — i18n dictionary
// Two languages: English (en) + Bahasa Malaysia (ms)
// All UI strings live here. Components consume via useT().

export const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      portfolio: "Portfolio",
      whyUs: "Why Choose Us",
      pricing: "Pricing",
      insights: "Insights",
      contact: "Contact",
      cta: "Get Free Consultation",
    },
    hero: {
      pill: "Trusted by Malaysian businesses",
      headlineA: "Professional Websites That Help",
      headlineB: "Malaysian Businesses",
      headlineC: "Grow",
      subheadline:
        "UrbanVista Web helps businesses build modern, fast, mobile-friendly websites that increase credibility, attract customers, and support long-term business growth.",
      ctaPrimary: "Get Your Website",
      ctaSecondary: "View Portfolio",
      trust: [
        "Mobile Responsive",
        "Fast Loading",
        "SEO Ready",
        "Professional Design",
      ],
      recentClients: "Recent clients",
      recentClientsSub: "SMEs across Malaysia",
      satisfaction: "Client satisfaction",
    },
    trust: {
      eyebrow: "Why work with us",
      title: "Trusted digital partner for growing businesses.",
      body:
        "UrbanVista Web creates websites focused on business credibility, customer trust, and measurable online growth. We combine clean design, fast performance, and clear copywriting to help your business stand out and convert.",
      stats: [
        { value: "50+", label: "Projects Delivered" },
        { value: "100%", label: "Responsive Design" },
        { value: "7-14", label: "Days Avg. Delivery" },
        { value: "24/7", label: "Reliable Support" },
      ],
    },
    services: {
      eyebrow: "Our services",
      title: "Everything your business needs to look great online.",
      body:
        "From your first website to ongoing support — we cover the full lifecycle so you can focus on running your business.",
      items: [
        {
          title: "Business Website Development",
          desc: "Professional websites designed for businesses, service companies, and SMEs that need a credible online presence.",
        },
        {
          title: "Website Redesign",
          desc: "Modernize outdated websites with better UX, faster speed, and trust-building design that converts.",
        },
        {
          title: "Landing Pages",
          desc: "High-converting landing pages built for campaigns, offers, and lead generation — focused on a single goal.",
        },
        {
          title: "Mobile Responsive Design",
          desc: "Pixel-perfect performance across phones, tablets, and desktops. Built mobile-first by default.",
        },
        {
          title: "SEO-Friendly Websites",
          desc: "Built with strong technical structure, fast performance, and search-friendly metadata from day one.",
        },
        {
          title: "Website Maintenance & Support",
          desc: "Ongoing updates, monitoring, and reliable support after launch so your website keeps performing.",
        },
      ],
    },
    portfolio: {
      eyebrow: "Selected work",
      title: "Real businesses. Real results.",
      body:
        "A snapshot of websites we've built for Malaysian businesses across industries — focused on credibility and conversion.",
      items: [
        {
          name: "Saji Modern Kitchen",
          industry: "Restaurant",
          location: "Kuala Lumpur",
          result: "Online reservations up 38% in the first 90 days.",
        },
        {
          name: "BrightSmile Dental",
          industry: "Dental Clinic",
          location: "Penang",
          result: "Doubled new patient enquiries with a faster, mobile-first site.",
        },
        {
          name: "Meridian Group",
          industry: "Corporate Business",
          location: "Petaling Jaya",
          result: "Premium brand presence and a clearer services pipeline.",
        },
        {
          name: "Forge Fitness Club",
          industry: "Gym & Fitness",
          location: "Johor Bahru",
          result: "Online membership sign-ups grew 2.4× quarter-on-quarter.",
        },
        {
          name: "Binaan Selatan Sdn Bhd",
          industry: "Construction",
          location: "Shah Alam",
          result: "Built credibility with project case studies and lead capture.",
        },
        {
          name: "Vista Realty",
          industry: "Real Estate",
          location: "Kuala Lumpur",
          result: "Polished listing pages helped close 17 sales in 6 months.",
        },
      ],
    },
    why: {
      eyebrow: "Why choose us",
      title: "Why businesses choose UrbanVista Web.",
      body:
        "We're not just designers — we're a long-term digital partner for business owners who want their online presence to actually drive growth.",
      items: [
        {
          title: "Professional Business Design",
          desc: "Clean, modern interfaces that build immediate trust with your customers.",
        },
        {
          title: "Fast Turnaround",
          desc: "Clear delivery timelines so you can launch and start growing sooner.",
        },
        {
          title: "Mobile-First Development",
          desc: "Designed for the way Malaysian customers actually browse — on mobile.",
        },
        {
          title: "Clear Communication",
          desc: "Straightforward updates in plain language, with no agency jargon.",
        },
        {
          title: "Reliable Support",
          desc: "Responsive post-launch support so your site keeps performing.",
        },
        {
          title: "Results-Oriented Websites",
          desc: "Every page is built to drive enquiries, bookings, or sales.",
        },
      ],
    },
    process: {
      eyebrow: "How we work",
      title: "Our simple process.",
      body:
        "A clear, structured path from first conversation to a live website that supports your business.",
      steps: [
        {
          title: "Discovery",
          desc: "We get to know your business, audience, and goals — and align on what success looks like.",
        },
        {
          title: "Design",
          desc: "We craft a modern, on-brand interface focused on credibility, clarity and conversion.",
        },
        {
          title: "Development",
          desc: "We build a fast, responsive and SEO-friendly website with clean structure throughout.",
        },
        {
          title: "Launch",
          desc: "We deploy, monitor and support your website — so it keeps working long after go-live.",
        },
      ],
    },
    pricing: {
      eyebrow: "Packages",
      title: "Transparent packages, tailored quotes.",
      body:
        "Every business is different. Choose a package that fits your stage and request a tailored quote — no obligation.",
      mostPopular: "Most Popular",
      cta: "Request Quote",
      footnoteLead: "Not sure which fits?",
      footnoteLink: "Book a free consultation",
      footnoteTail:
        "and we'll recommend the right package for your business.",
      plans: [
        {
          name: "Starter",
          tagline: "For small businesses establishing their online presence.",
          features: [
            "Up to 5 professional pages",
            "Mobile responsive design",
            "Contact form & WhatsApp integration",
            "Basic on-page SEO",
            "1 round of revisions",
            "Domain & hosting setup support",
          ],
        },
        {
          name: "Growth",
          tagline: "For growing companies that need more depth and credibility.",
          features: [
            "Up to 10 custom-designed pages",
            "Premium mobile-first design",
            "Lead capture & enquiry forms",
            "Advanced on-page SEO",
            "Blog / news section ready",
            "2 rounds of revisions",
            "30 days post-launch support",
          ],
        },
        {
          name: "Premium",
          tagline: "For established businesses that want a flagship website.",
          features: [
            "Fully custom website build",
            "Advanced animations & interactions",
            "CMS for self-managed content",
            "Performance & SEO optimization",
            "Analytics & conversion setup",
            "Unlimited revisions during build",
            "90 days priority support",
          ],
        },
      ],
    },
    testimonials: {
      eyebrow: "Client stories",
      title: "Trusted by business owners across Malaysia.",
      items: [
        {
          quote:
            "The new website looks far more professional than what we had before. Bookings through the site have grown steadily, and customers often mention how easy it is to find our menu and location.",
          name: "Aiman Hakim",
          role: "Owner",
          business: "Saji Modern Kitchen — Kuala Lumpur",
          initials: "AH",
        },
        {
          quote:
            "Communication was clear from day one and delivery was on time. New patient enquiries through our website have nearly doubled since the redesign went live.",
          name: "Dr. Lim Wei Sheng",
          role: "Founder",
          business: "BrightSmile Dental — Penang",
          initials: "LW",
        },
        {
          quote:
            "We finally have a website that reflects the quality of our shop. The mobile experience in particular has helped us convert walk-bys into walk-ins.",
          name: "Nurul Aisyah",
          role: "Retail Business Owner",
          business: "Aisyah Boutique — Johor Bahru",
          initials: "NA",
        },
      ],
    },
    insights: {
      eyebrow: "Insights",
      title: "Practical advice for Malaysian business owners.",
      body:
        "Short, useful articles on building a website that actually grows your business — no jargon, no fluff.",
      readMore: "Read more",
      comingSoon: "Article coming soon",
      comingSoonDesc:
        "We're publishing new articles each month. Subscribe via WhatsApp to be notified.",
      items: [
        {
          category: "SME Guide",
          date: "12 Dec 2025",
          readTime: "6 min read",
          title:
            "Why Mobile-First Design Matters for Malaysian SMEs in 2026",
          excerpt:
            "More than 80% of customers in Malaysia browse on their phone first. Here's how to design a website that wins on mobile before desktop.",
        },
        {
          category: "Conversion",
          date: "28 Nov 2025",
          readTime: "5 min read",
          title:
            "The 7 Trust Signals Every Business Website Must Have",
          excerpt:
            "From clear contact details to social proof — the small details that quietly turn visitors into paying customers.",
        },
        {
          category: "SEO",
          date: "14 Nov 2025",
          readTime: "8 min read",
          title:
            "Local SEO Basics: How To Get Found On Google In KL & Selangor",
          excerpt:
            "A no-nonsense walkthrough for local businesses who want to rank for searches in their city, not just nationwide.",
        },
      ],
    },
    faq: {
      eyebrow: "Frequently asked questions",
      title: "Got questions? We've got answers.",
      items: [
        {
          q: "How long does website development take?",
          a: "Most small business websites are delivered in 2-3 weeks. Larger or more complex projects typically take 4-6 weeks. After our first discovery call, we'll share a precise timeline based on your scope.",
        },
        {
          q: "Will my website work on mobile?",
          a: "Yes — every site we build is mobile-first, meaning we design and test for phones and tablets before desktop. Your customers will get a polished experience on every device.",
        },
        {
          q: "Do you provide support after launch?",
          a: "Absolutely. Every package includes a post-launch support window, and we also offer ongoing maintenance plans for content updates, security, and small design tweaks.",
        },
        {
          q: "Can you redesign my current website?",
          a: "Yes. We frequently modernize outdated websites — improving speed, mobile responsiveness, copywriting, and conversion. We'll audit your current site and recommend a redesign plan.",
        },
        {
          q: "How do we start?",
          a: "Send us a message through the form below or WhatsApp. We'll arrange a free consultation to understand your goals, walk you through your options, and send a clear, written proposal.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact us",
      title: "Let's build your business website.",
      body:
        "Tell us a little about your business and what you need. We'll reply within one business day with a clear, no-obligation consultation.",
      phoneLabel: "Phone / WhatsApp",
      emailLabel: "Email",
      servingLabel: "Serving",
      servingValue: "Businesses across Malaysia",
      whatsappCta: "Chat on WhatsApp",
      emailCta: "Email Us",
      whatsappPrefill: "Hi UrbanVista Web, I'd like to know more about your services.",
      emailSubject: "Website enquiry",
      formBadge: "Book a free consultation",
      fields: {
        name: "Name",
        namePlaceholder: "Your full name",
        business: "Business Name",
        businessPlaceholder: "e.g. Saji Modern Kitchen",
        email: "Email",
        emailPlaceholder: "you@business.com",
        phone: "Phone Number",
        phonePlaceholder: "+60 12 345 6789",
        message: "Message",
        messagePlaceholder:
          "Tell us about your business and what you need help with...",
      },
      submit: "Book A Free Consultation",
      submitting: "Sending...",
      submitted: "Message Sent",
      successTitle: "Thank you! Your message has been received.",
      successDesc: "We'll get back to you within one business day.",
      errorTitle: "Could not send message",
      errorGeneric: "Something went wrong sending your message.",
      networkErrorTitle: "Network error",
      networkErrorDesc: "Please try again or contact us via WhatsApp.",
      privacyNote: "We'll reply within one business day. Your details stay private.",
    },
    footer: {
      brandStatement:
        "Professional websites for modern businesses. Built for credibility, speed, and real business growth.",
      navigate: "Navigate",
      getInTouch: "Get in touch",
      cta: "Get Free Consultation",
      rights: "© 2026 UrbanVista Web. All Rights Reserved.",
      tagline: "Modern Websites. Real Business Growth.",
    },
    cookie: {
      title: "We value your privacy",
      body:
        "We use cookies to improve your browsing experience, analyse site traffic, and personalise content. In line with Malaysia's Personal Data Protection Act (PDPA) 2010, we will only process your personal data with your consent.",
      accept: "Accept all",
      decline: "Decline",
      learnMore: "Learn more",
    },
    common: {
      langName: "EN",
      switchTo: "BM",
    },
  },

  ms: {
    nav: {
      home: "Utama",
      services: "Perkhidmatan",
      portfolio: "Portfolio",
      whyUs: "Mengapa Pilih Kami",
      pricing: "Harga",
      insights: "Wawasan",
      contact: "Hubungi",
      cta: "Konsultasi Percuma",
    },
    hero: {
      pill: "Dipercayai oleh perniagaan di Malaysia",
      headlineA: "Laman Web Profesional Yang Membantu",
      headlineB: "Perniagaan Malaysia",
      headlineC: "Berkembang",
      subheadline:
        "UrbanVista Web membantu perniagaan membina laman web moden, pantas dan mesra mobile yang meningkatkan kredibiliti, menarik pelanggan, serta menyokong pertumbuhan jangka panjang.",
      ctaPrimary: "Dapatkan Laman Anda",
      ctaSecondary: "Lihat Portfolio",
      trust: [
        "Mesra Mobile",
        "Pemuatan Pantas",
        "Sedia SEO",
        "Reka Bentuk Profesional",
      ],
      recentClients: "Klien terkini",
      recentClientsSub: "PKS di seluruh Malaysia",
      satisfaction: "Kepuasan klien",
    },
    trust: {
      eyebrow: "Mengapa bekerjasama dengan kami",
      title: "Rakan digital yang dipercayai untuk perniagaan yang berkembang.",
      body:
        "UrbanVista Web mencipta laman web yang fokus kepada kredibiliti perniagaan, kepercayaan pelanggan, dan pertumbuhan dalam talian yang dapat diukur. Kami menggabungkan reka bentuk bersih, prestasi pantas dan penulisan yang jelas untuk membantu perniagaan anda menonjol.",
      stats: [
        { value: "50+", label: "Projek Disiapkan" },
        { value: "100%", label: "Reka Bentuk Responsif" },
        { value: "7-14", label: "Hari Purata Penghantaran" },
        { value: "24/7", label: "Sokongan Yang Dipercayai" },
      ],
    },
    services: {
      eyebrow: "Perkhidmatan kami",
      title: "Semua yang perniagaan anda perlukan untuk kelihatan hebat dalam talian.",
      body:
        "Daripada laman web pertama anda hingga sokongan berterusan — kami merangkumi keseluruhan kitaran supaya anda boleh fokus menguruskan perniagaan.",
      items: [
        {
          title: "Pembangunan Laman Web Perniagaan",
          desc: "Laman web profesional untuk perniagaan, syarikat perkhidmatan dan PKS yang memerlukan kehadiran dalam talian yang kredibel.",
        },
        {
          title: "Reka Bentuk Semula Laman Web",
          desc: "Memodenkan laman web lama dengan UX yang lebih baik, kelajuan dan reka bentuk yang membina kepercayaan.",
        },
        {
          title: "Halaman Pendaratan",
          desc: "Halaman pendaratan berkesan untuk kempen, tawaran dan penjanaan lead — fokus pada satu matlamat.",
        },
        {
          title: "Reka Bentuk Mesra Mobile",
          desc: "Prestasi sempurna pada telefon, tablet dan desktop. Dibina mengutamakan mobile.",
        },
        {
          title: "Laman Web Mesra SEO",
          desc: "Dibina dengan struktur teknikal kukuh, prestasi pantas dan metadata yang sesuai untuk enjin carian.",
        },
        {
          title: "Penyelenggaraan & Sokongan",
          desc: "Kemas kini, pemantauan dan sokongan yang berterusan selepas pelancaran.",
        },
      ],
    },
    portfolio: {
      eyebrow: "Kerja terpilih",
      title: "Perniagaan sebenar. Hasil sebenar.",
      body:
        "Gambaran ringkas laman web yang kami bina untuk perniagaan Malaysia merentas industri — fokus kepada kredibiliti dan penukaran.",
      items: [
        {
          name: "Saji Modern Kitchen",
          industry: "Restoran",
          location: "Kuala Lumpur",
          result: "Tempahan dalam talian meningkat 38% dalam 90 hari pertama.",
        },
        {
          name: "BrightSmile Dental",
          industry: "Klinik Pergigian",
          location: "Pulau Pinang",
          result: "Pertanyaan pesakit baharu berganda dengan laman mesra mobile.",
        },
        {
          name: "Meridian Group",
          industry: "Perniagaan Korporat",
          location: "Petaling Jaya",
          result: "Kehadiran jenama premium dan saluran perkhidmatan yang jelas.",
        },
        {
          name: "Forge Fitness Club",
          industry: "Gym & Kecergasan",
          location: "Johor Bahru",
          result: "Pendaftaran keahlian dalam talian meningkat 2.4× setiap suku.",
        },
        {
          name: "Binaan Selatan Sdn Bhd",
          industry: "Pembinaan",
          location: "Shah Alam",
          result: "Membina kredibiliti melalui kajian kes dan tangkapan lead.",
        },
        {
          name: "Vista Realty",
          industry: "Hartanah",
          location: "Kuala Lumpur",
          result: "Halaman senarai yang kemas membantu menutup 17 jualan dalam 6 bulan.",
        },
      ],
    },
    why: {
      eyebrow: "Mengapa pilih kami",
      title: "Mengapa perniagaan memilih UrbanVista Web.",
      body:
        "Kami bukan sekadar pereka — kami rakan digital jangka panjang untuk pemilik perniagaan yang mahu kehadiran dalam talian memacu pertumbuhan sebenar.",
      items: [
        {
          title: "Reka Bentuk Perniagaan Profesional",
          desc: "Antara muka moden dan bersih yang membina kepercayaan pelanggan dengan segera.",
        },
        {
          title: "Penghantaran Pantas",
          desc: "Garis masa penghantaran yang jelas supaya anda boleh melancarkan dan berkembang lebih cepat.",
        },
        {
          title: "Pembangunan Mengutamakan Mobile",
          desc: "Direka mengikut cara pelanggan Malaysia melayari — di mobile.",
        },
        {
          title: "Komunikasi Yang Jelas",
          desc: "Kemas kini ringkas dalam bahasa mudah, tanpa jargon agensi.",
        },
        {
          title: "Sokongan Yang Dipercayai",
          desc: "Sokongan pasca-pelancaran yang responsif supaya laman anda terus berfungsi.",
        },
        {
          title: "Laman Berorientasikan Hasil",
          desc: "Setiap halaman dibina untuk memacu pertanyaan, tempahan atau jualan.",
        },
      ],
    },
    process: {
      eyebrow: "Cara kami bekerja",
      title: "Proses ringkas kami.",
      body:
        "Jalur yang jelas dan tersusun daripada perbualan pertama sehingga laman web hidup yang menyokong perniagaan anda.",
      steps: [
        {
          title: "Penemuan",
          desc: "Kami mengenali perniagaan, audiens dan matlamat anda — serta menyelaraskan definisi kejayaan.",
        },
        {
          title: "Reka Bentuk",
          desc: "Kami mereka antara muka moden dan berjenama yang fokus pada kredibiliti, kejelasan dan penukaran.",
        },
        {
          title: "Pembangunan",
          desc: "Kami membina laman web pantas, responsif dan mesra SEO dengan struktur yang bersih.",
        },
        {
          title: "Pelancaran",
          desc: "Kami menerbit, memantau dan menyokong laman anda — supaya ia terus berfungsi selepas dilancarkan.",
        },
      ],
    },
    pricing: {
      eyebrow: "Pakej",
      title: "Pakej telus, sebut harga disesuaikan.",
      body:
        "Setiap perniagaan berbeza. Pilih pakej yang sesuai dengan peringkat anda dan minta sebut harga — tanpa obligasi.",
      mostPopular: "Paling Popular",
      cta: "Minta Sebut Harga",
      footnoteLead: "Tidak pasti yang mana sesuai?",
      footnoteLink: "Tempah konsultasi percuma",
      footnoteTail:
        "dan kami akan mengesyorkan pakej yang sesuai untuk perniagaan anda.",
      plans: [
        {
          name: "Permulaan",
          tagline: "Untuk perniagaan kecil yang membina kehadiran dalam talian.",
          features: [
            "Sehingga 5 halaman profesional",
            "Reka bentuk mesra mobile",
            "Borang hubungi & integrasi WhatsApp",
            "SEO halaman asas",
            "1 pusingan semakan",
            "Sokongan persediaan domain & hosting",
          ],
        },
        {
          name: "Pertumbuhan",
          tagline: "Untuk syarikat yang berkembang dan memerlukan kredibiliti lebih.",
          features: [
            "Sehingga 10 halaman tersuai",
            "Reka bentuk mengutamakan mobile premium",
            "Borang tangkapan lead & pertanyaan",
            "SEO halaman lanjutan",
            "Sedia bahagian blog / berita",
            "2 pusingan semakan",
            "Sokongan 30 hari selepas pelancaran",
          ],
        },
        {
          name: "Premium",
          tagline: "Untuk perniagaan mapan yang mahu laman web utama.",
          features: [
            "Pembinaan laman web sepenuhnya tersuai",
            "Animasi & interaksi lanjutan",
            "CMS untuk pengurusan kandungan sendiri",
            "Pengoptimuman prestasi & SEO",
            "Persediaan analitik & penukaran",
            "Semakan tanpa had semasa pembinaan",
            "Sokongan keutamaan 90 hari",
          ],
        },
      ],
    },
    testimonials: {
      eyebrow: "Kisah klien",
      title: "Dipercayai oleh pemilik perniagaan di seluruh Malaysia.",
      items: [
        {
          quote:
            "Laman web baharu kelihatan jauh lebih profesional. Tempahan melalui laman terus bertambah, dan pelanggan sering menyebut betapa mudah mencari menu dan lokasi kami.",
          name: "Aiman Hakim",
          role: "Pemilik",
          business: "Saji Modern Kitchen — Kuala Lumpur",
          initials: "AH",
        },
        {
          quote:
            "Komunikasi jelas dari hari pertama dan penghantaran tepat pada masanya. Pertanyaan pesakit baharu hampir berganda sejak laman direka semula.",
          name: "Dr. Lim Wei Sheng",
          role: "Pengasas",
          business: "BrightSmile Dental — Pulau Pinang",
          initials: "LW",
        },
        {
          quote:
            "Akhirnya kami ada laman web yang mencerminkan kualiti kedai. Pengalaman mobile khususnya membantu kami menukar peminat menjadi pelanggan.",
          name: "Nurul Aisyah",
          role: "Pemilik Perniagaan Runcit",
          business: "Aisyah Boutique — Johor Bahru",
          initials: "NA",
        },
      ],
    },
    insights: {
      eyebrow: "Wawasan",
      title: "Nasihat praktikal untuk pemilik perniagaan Malaysia.",
      body:
        "Artikel ringkas dan berguna tentang membina laman web yang benar-benar membantu perniagaan anda berkembang — tanpa jargon.",
      readMore: "Baca lagi",
      comingSoon: "Artikel akan datang",
      comingSoonDesc:
        "Kami menerbitkan artikel baharu setiap bulan. Langgan melalui WhatsApp untuk dimaklumkan.",
      items: [
        {
          category: "Panduan PKS",
          date: "12 Dis 2025",
          readTime: "6 minit",
          title:
            "Mengapa Reka Bentuk Mengutamakan Mobile Penting Untuk PKS Malaysia 2026",
          excerpt:
            "Lebih 80% pelanggan di Malaysia melayari melalui telefon dahulu. Begini cara mereka bentuk laman yang menang di mobile sebelum desktop.",
        },
        {
          category: "Penukaran",
          date: "28 Nov 2025",
          readTime: "5 minit",
          title:
            "7 Tanda Kepercayaan Yang Setiap Laman Perniagaan Perlu Ada",
          excerpt:
            "Daripada maklumat hubungan yang jelas hingga bukti sosial — butiran kecil yang mengubah pelawat menjadi pelanggan.",
        },
        {
          category: "SEO",
          date: "14 Nov 2025",
          readTime: "8 minit",
          title:
            "Asas SEO Tempatan: Bagaimana Ditemui Di Google Untuk KL & Selangor",
          excerpt:
            "Panduan ringkas untuk perniagaan tempatan yang mahu disenaraikan untuk carian di bandar mereka.",
        },
      ],
    },
    faq: {
      eyebrow: "Soalan lazim",
      title: "Ada soalan? Kami ada jawapan.",
      items: [
        {
          q: "Berapa lama masa diambil untuk membina laman web?",
          a: "Kebanyakan laman web perniagaan kecil disiapkan dalam 2-3 minggu. Projek yang lebih besar atau kompleks biasanya mengambil 4-6 minggu. Selepas perbincangan awal, kami akan kongsi garis masa yang tepat.",
        },
        {
          q: "Adakah laman saya berfungsi di mobile?",
          a: "Ya — setiap laman yang kami bina adalah mengutamakan mobile, bermaksud kami reka dan uji untuk telefon serta tablet sebelum desktop.",
        },
        {
          q: "Adakah anda menyediakan sokongan selepas pelancaran?",
          a: "Sudah tentu. Setiap pakej termasuk tempoh sokongan selepas pelancaran, dan kami juga menawarkan pelan penyelenggaraan berterusan.",
        },
        {
          q: "Bolehkah anda mereka bentuk semula laman saya yang sedia ada?",
          a: "Ya. Kami kerap memodenkan laman web lama — meningkatkan kelajuan, responsif mobile, penulisan dan penukaran.",
        },
        {
          q: "Bagaimana kita bermula?",
          a: "Hantar mesej melalui borang di bawah atau WhatsApp. Kami akan mengatur konsultasi percuma untuk memahami matlamat anda dan menghantar cadangan bertulis.",
        },
      ],
    },
    contact: {
      eyebrow: "Hubungi kami",
      title: "Mari bina laman web perniagaan anda.",
      body:
        "Beritahu kami sedikit tentang perniagaan anda dan apa yang anda perlukan. Kami akan membalas dalam satu hari bekerja dengan konsultasi tanpa obligasi.",
      phoneLabel: "Telefon / WhatsApp",
      emailLabel: "E-mel",
      servingLabel: "Berkhidmat",
      servingValue: "Perniagaan di seluruh Malaysia",
      whatsappCta: "Sembang di WhatsApp",
      emailCta: "Hantar E-mel",
      whatsappPrefill:
        "Hai UrbanVista Web, saya ingin tahu lebih lanjut tentang perkhidmatan anda.",
      emailSubject: "Pertanyaan laman web",
      formBadge: "Tempah konsultasi percuma",
      fields: {
        name: "Nama",
        namePlaceholder: "Nama penuh anda",
        business: "Nama Perniagaan",
        businessPlaceholder: "cth. Saji Modern Kitchen",
        email: "E-mel",
        emailPlaceholder: "anda@perniagaan.com",
        phone: "Nombor Telefon",
        phonePlaceholder: "+60 12 345 6789",
        message: "Mesej",
        messagePlaceholder:
          "Beritahu kami tentang perniagaan anda dan apa yang anda perlukan...",
      },
      submit: "Tempah Konsultasi Percuma",
      submitting: "Menghantar...",
      submitted: "Mesej Dihantar",
      successTitle: "Terima kasih! Mesej anda telah diterima.",
      successDesc: "Kami akan membalas dalam satu hari bekerja.",
      errorTitle: "Gagal menghantar mesej",
      errorGeneric: "Sesuatu telah berlaku semasa menghantar mesej anda.",
      networkErrorTitle: "Ralat rangkaian",
      networkErrorDesc: "Sila cuba lagi atau hubungi kami melalui WhatsApp.",
      privacyNote:
        "Kami akan membalas dalam satu hari bekerja. Maklumat anda kekal peribadi.",
    },
    footer: {
      brandStatement:
        "Laman web profesional untuk perniagaan moden. Dibina untuk kredibiliti, kelajuan dan pertumbuhan sebenar.",
      navigate: "Navigasi",
      getInTouch: "Hubungi kami",
      cta: "Konsultasi Percuma",
      rights: "© 2026 UrbanVista Web. Hak Cipta Terpelihara.",
      tagline: "Laman Moden. Pertumbuhan Perniagaan Sebenar.",
    },
    cookie: {
      title: "Kami menghargai privasi anda",
      body:
        "Kami menggunakan kuki untuk meningkatkan pengalaman pelayaran anda, menganalisis trafik laman, dan memperibadikan kandungan. Selaras dengan Akta Perlindungan Data Peribadi (PDPA) Malaysia 2010, kami hanya akan memproses data peribadi anda dengan persetujuan anda.",
      accept: "Terima semua",
      decline: "Tolak",
      learnMore: "Ketahui lebih lanjut",
    },
    common: {
      langName: "BM",
      switchTo: "EN",
    },
  },
};

export default translations;
